import { useAuth, useAuthenticatedFetch } from "@stardeck-customer-apps/project-auth";
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  Loader2,
  TrendingUp,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { AdminLayout } from "@/components/admin-layout";

interface Reservation {
  id: number;
  guest_name: string;
  villa_id: number;
  villa_name: string;
  check_in_date: string;
  check_out_date: string;
  status: string;
  total_amount: string | null;
}

export function AdminCalendarPage() {
  const { user, isLoading: isAuthLoading } = useAuth();
  const navigate = useNavigate();
  const authenticatedFetch = useAuthenticatedFetch();
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Protect route - redirect if not authenticated
  useEffect(() => {
    if (!isAuthLoading && !user) {
      navigate("/login");
    }
  }, [user, isAuthLoading, navigate]);

  useEffect(() => {
    if (user) {
      fetchReservations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonth, currentYear, user]);

  const fetchReservations = async () => {
    setIsLoading(true);
    try {
      // Fetch all reservations for the current month
      const firstDay = new Date(currentYear, currentMonth, 1).toISOString().split("T")[0];
      const lastDay = new Date(currentYear, currentMonth + 1, 0).toISOString().split("T")[0];

      const response = await authenticatedFetch(`/api/admin/reservations?limit=1000`);

      if (response.status === 401) {
        navigate("/login");
        return;
      }

      if (response.status === 403) {
        navigate("/unauthorized");
        return;
      }

      const data = (await response.json()) as {
        success: boolean;
        reservations: Reservation[];
      };

      if (data.success) {
        // Filter for current month
        const filtered = data.reservations.filter((r: Reservation) => {
          const checkIn = new Date(r.check_in_date);
          const checkOut = new Date(r.check_out_date);
          const monthStart = new Date(firstDay);
          const monthEnd = new Date(lastDay);

          return (
            r.status !== "cancelled" &&
            ((checkIn >= monthStart && checkIn <= monthEnd) ||
              (checkOut >= monthStart && checkOut <= monthEnd) ||
              (checkIn <= monthStart && checkOut >= monthEnd))
          );
        });

        setReservations(filtered);
      }
    } catch (error) {
      console.error("Failed to fetch reservations:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreviousMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year: number, month: number) => {
    return new Date(year, month, 1).getDay();
  };

  const monthName = new Date(currentYear, currentMonth).toLocaleString("default", {
    month: "long",
  });
  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDayOfMonth = getFirstDayOfMonth(currentYear, currentMonth);

  // Calculate stats
  const totalRevenue = reservations.reduce((sum, r) => {
    return sum + (r.total_amount ? parseFloat(r.total_amount) : 0);
  }, 0);

  const confirmedReservations = reservations.filter((r) => r.status === "confirmed").length;

  // Calculate occupancy rate
  const totalVillas = 8;
  const totalNights = daysInMonth * totalVillas;
  let bookedNights = 0;

  reservations.forEach((r) => {
    if (r.status === "confirmed") {
      const checkIn = new Date(r.check_in_date);
      const checkOut = new Date(r.check_out_date);
      const monthStart = new Date(currentYear, currentMonth, 1);
      const monthEnd = new Date(currentYear, currentMonth + 1, 0);

      const effectiveStart = checkIn < monthStart ? monthStart : checkIn;
      const effectiveEnd = checkOut > monthEnd ? monthEnd : checkOut;

      const nights = Math.ceil(
        (effectiveEnd.getTime() - effectiveStart.getTime()) / (1000 * 60 * 60 * 24)
      );
      bookedNights += Math.max(0, nights);
    }
  });

  const occupancyRate = ((bookedNights / totalNights) * 100).toFixed(1);

  // Get villa colors
  const villaColors = [
    "bg-blue-100 border-blue-300 text-blue-900",
    "bg-green-100 border-green-300 text-green-900",
    "bg-purple-100 border-purple-300 text-purple-900",
    "bg-amber-100 border-amber-300 text-amber-900",
    "bg-rose-100 border-rose-300 text-rose-900",
    "bg-cyan-100 border-cyan-300 text-cyan-900",
    "bg-indigo-100 border-indigo-300 text-indigo-900",
    "bg-pink-100 border-pink-300 text-pink-900",
  ];

  const getReservationsForDay = (day: number) => {
    const date = new Date(currentYear, currentMonth, day);
    return reservations.filter((r) => {
      const checkIn = new Date(r.check_in_date);
      const checkOut = new Date(r.check_out_date);
      return date >= checkIn && date < checkOut && r.status !== "cancelled";
    });
  };

  // Show loading spinner while checking authentication
  if (isAuthLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="size-8 animate-spin text-stone-400" />
      </div>
    );
  }

  // Don't render if not authenticated (will redirect)
  if (!user) {
    return null;
  }

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-['Libre_Baskerville'] text-3xl font-bold text-stone-900 mb-1">
            Calendar Overview
          </h1>
          <p className="text-stone-600">Visualize bookings and occupancy across all villas</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white border border-stone-200 p-6 rounded-lg">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-green-100 rounded">
                <TrendingUp className="size-5 text-green-700" />
              </div>
              <div className="text-sm text-stone-600">Occupancy Rate</div>
            </div>
            <div className="text-3xl font-bold text-stone-900">{occupancyRate}%</div>
            <div className="text-xs text-stone-500 mt-1">
              {bookedNights} of {totalNights} nights booked
            </div>
          </div>

          <div className="bg-white border border-stone-200 p-6 rounded-lg">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-100 rounded">
                <CalendarIcon className="size-5 text-blue-700" />
              </div>
              <div className="text-sm text-stone-600">Reservations</div>
            </div>
            <div className="text-3xl font-bold text-stone-900">{confirmedReservations}</div>
            <div className="text-xs text-stone-500 mt-1">Confirmed bookings this month</div>
          </div>

          <div className="bg-white border border-stone-200 p-6 rounded-lg">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-amber-100 rounded">
                <DollarSign className="size-5 text-amber-700" />
              </div>
              <div className="text-sm text-stone-600">Revenue</div>
            </div>
            <div className="text-3xl font-bold text-stone-900">${totalRevenue.toFixed(0)}</div>
            <div className="text-xs text-stone-500 mt-1">Total for this month</div>
          </div>
        </div>

        {/* Calendar */}
        <div className="bg-white border border-stone-200 rounded-lg p-6">
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={handlePreviousMonth}
              className="flex items-center gap-2 text-stone-600 hover:text-stone-900 transition-colors"
            >
              <ChevronLeft className="size-5" />
              Previous
            </button>
            <h3 className="font-semibold text-xl">
              {monthName} {currentYear}
            </h3>
            <button
              onClick={handleNextMonth}
              className="flex items-center gap-2 text-stone-600 hover:text-stone-900 transition-colors"
            >
              Next
              <ChevronRight className="size-5" />
            </button>
          </div>

          {isLoading ? (
            <div className="py-12 text-center">
              <div className="inline-block animate-spin rounded-full size-8 border-b-2 border-stone-900" />
            </div>
          ) : (
            <>
              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-2">
                {/* Day headers */}
                {["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"].map((day) => (
                  <div key={day} className="text-center font-semibold text-xs text-stone-500 py-2">
                    {day}
                  </div>
                ))}

                {/* Empty cells */}
                {Array.from({ length: firstDayOfMonth }).map((_, i) => (
                  <div key={`empty-${i}`} className="aspect-square" />
                ))}

                {/* Calendar days */}
                {Array.from({ length: daysInMonth }).map((_, i) => {
                  const day = i + 1;
                  const dayReservations = getReservationsForDay(day);

                  return (
                    <div
                      key={day}
                      className="border border-stone-200 rounded p-2 aspect-square flex flex-col"
                    >
                      <div className="text-sm font-semibold text-stone-900 mb-1">{day}</div>
                      <div className="flex-1 overflow-y-auto space-y-1">
                        {dayReservations.map((reservation) => (
                          <div
                            key={reservation.id}
                            className={`text-xs px-1.5 py-1 rounded border ${villaColors[(reservation.villa_id - 1) % villaColors.length]}`}
                            title={`${reservation.guest_name} - ${reservation.villa_name}`}
                          >
                            <div className="font-semibold truncate">V{reservation.villa_id}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="mt-6 pt-6 border-t border-stone-200">
                <div className="text-xs font-semibold text-stone-600 mb-3">VILLAS</div>
                <div className="flex flex-wrap gap-3">
                  {Array.from({ length: 8 }, (_, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className={`size-4 rounded border ${villaColors[i]}`} />
                      <span className="text-xs text-stone-600">Villa {i + 1}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
